/**
 * PATH: packages/email-hub/src/feature/components/EstimatorAllocator.tsx
 * PURPOSE: "Allocate estimator" control for quote-request emails, shown in
 *          the reader's AI-triage header area. Picking an estimator hands
 *          the email off to them — which (server-side) auto-grants them
 *          visibility AND fires an email ping so they go prepare the
 *          tender/quote.
 * WHY:     Operator (2026-06-01): "If it's a quote request, in that white
 *          space next to the email header add 'allocate an estimator' who
 *          gets sent an email to do this tender."
 * BACKEND: Reuses the Stack-11 handoff endpoints (no FE wiring existed):
 *            - GET  /api/emails/<id>/assign/           → who's allocated
 *            - POST /api/emails/<id>/assign/           → allocate + email
 *            - GET  /api/emails/assignable-members/    → org teammates
 *          The POST path sends the SMTP "you've been assigned an email"
 *          notification; that IS the "gets sent an email" the operator
 *          asked for.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Loader2, Mail, UserPlus } from 'lucide-react'
import { emailApi } from '../lib/api'
import { useToast } from '../../_internal/components/Toast'

interface Assignee {
  id: string
  assignee_user_id: number
  assignee_name: string | null
  assignee_email: string
  notification_sent_at: string | null
  notification_error: string
}

interface AssignableMember {
  user_id: number
  display_name: string
  email: string
  role: string
  /** Free-text job title (e.g. "Estimator"). Drives the estimators-first
   *  default in the picker. May be '' for members who haven't set one. */
  position: string
}

/** Default note carried into the in-app + SMTP notification. Kept generic
 *  enough for any quote request / tender; the estimator gets a deep link
 *  back to the email in the body. */
const DEFAULT_MESSAGE =
  "You've been allocated this quote request. Please review the email and prepare the tender/quote."

function firstName(name: string | null | undefined, fallback = 'the estimator'): string {
  const n = (name || '').trim()
  if (!n) return fallback
  return n.split(/\s+/)[0]
}

/** A member counts as an estimator when their job-title position contains
 *  "estimat" (Estimator / Estimating / Senior Estimator …), case-
 *  insensitive. The picker defaults to these so allocating a tender lands
 *  on the right people; a "Show all" toggle reveals the rest. */
function isEstimator(m: AssignableMember): boolean {
  return /estimat/i.test(m.position || '')
}

export function EstimatorAllocator({
  emailId,
  onAllocated,
}: {
  emailId: string
  /** Host refetch so the surrounding list/preview stays consistent. */
  onAllocated?: () => void
}) {
  const { toast } = useToast()
  const [assignees, setAssignees] = useState<Assignee[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [members, setMembers] = useState<AssignableMember[] | null>(null)
  const [membersLoading, setMembersLoading] = useState(false)
  const [busyUserId, setBusyUserId] = useState<number | null>(null)
  // When the org tags estimators, the picker shows only them by default;
  // "Show all" flips this to reveal the whole team.
  const [showAll, setShowAll] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Load current allocations for this email. Swallows errors (e.g. the GET
  // endpoint not yet deployed) — the control still offers allocation.
  const loadAssignees = useCallback(async () => {
    try {
      const data = await emailApi<{ results: Assignee[] }>(
        `/api/emails/${emailId}/assign/`,
      )
      return data?.results ?? []
    } catch {
      return []
    }
  }, [emailId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setOpen(false)
    loadAssignees().then((rows) => {
      if (cancelled) return
      setAssignees(rows)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [loadAssignees])

  // Close the picker on outside click / Escape.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const openPicker = useCallback(async () => {
    setOpen((v) => !v)
    setShowAll(false)
    if (members !== null || membersLoading) return
    setMembersLoading(true)
    try {
      const data = await emailApi<{ results: AssignableMember[] }>(
        '/api/emails/assignable-members/',
      )
      setMembers(data?.results ?? [])
    } catch {
      setMembers([])
      toast('Could not load teammates to allocate.', 'error')
    } finally {
      setMembersLoading(false)
    }
  }, [members, membersLoading, toast])

  const allocate = useCallback(
    async (member: AssignableMember) => {
      setBusyUserId(member.user_id)
      try {
        const created = await emailApi<Assignee>(
          `/api/emails/${emailId}/assign/`,
          {
            method: 'POST',
            body: JSON.stringify({
              assignee_user_id: member.user_id,
              message: DEFAULT_MESSAGE,
            }),
          },
        )
        const emailed = !!created?.notification_sent_at
        const who = firstName(created?.assignee_name || member.display_name)
        toast(
          emailed
            ? `Allocated to ${member.display_name} - emailed ${who} to prepare the quote.`
            : `Allocated to ${member.display_name}. (In-app notified; email ping didn't send.)`,
          emailed ? 'success' : 'warning',
        )
        setOpen(false)
        const rows = await loadAssignees()
        setAssignees(rows)
        onAllocated?.()
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Allocation failed'
        toast(`Could not allocate estimator: ${msg}`, 'error')
      } finally {
        setBusyUserId(null)
      }
    },
    [emailId, loadAssignees, onAllocated, toast],
  )

  // De-dupe: latest allocation per assignee (newest-first from the API),
  // so re-assigning the same person doesn't show twice.
  const current: Assignee[] = (() => {
    const seen = new Set<number>()
    const out: Assignee[] = []
    for (const a of assignees) {
      if (seen.has(a.assignee_user_id)) continue
      seen.add(a.assignee_user_id)
      out.push(a)
    }
    return out
  })()

  const assignedIds = new Set(current.map((a) => a.assignee_user_id))
  const pickable = (members ?? []).filter((m) => !assignedIds.has(m.user_id))
  // Estimators-first: if any pickable member is tagged as an estimator we
  // default the list to just them, with a toggle to reveal everyone. Orgs
  // that don't set positions fall through to the whole team (no toggle),
  // so the picker is never mysteriously empty.
  const estimators = pickable.filter(isEstimator)
  const hasEstimators = estimators.length > 0
  const others = pickable.filter((m) => !isEstimator(m))
  const showingAll = showAll || !hasEstimators
  const visibleMembers = showingAll ? pickable : estimators

  return (
    <div ref={rootRef} className="relative mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
      <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400 shrink-0">
        <UserPlus className="h-3 w-3" aria-hidden />
        Estimator
      </span>

      {loading ? (
        <span className="inline-flex items-center gap-1 text-muted-foreground/60">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        </span>
      ) : (
        <>
          {current.map((a) => (
            <span
              key={a.id}
              title={
                a.notification_sent_at
                  ? `Emailed ${a.assignee_email || a.assignee_name || ''}`
                  : 'Allocated (email ping not confirmed)'
              }
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 max-w-[220px]"
            >
              <Check className="h-2.5 w-2.5 shrink-0" aria-hidden />
              <span className="truncate">{a.assignee_name || a.assignee_email || `User ${a.assignee_user_id}`}</span>
              {a.notification_sent_at && (
                <Mail className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden />
              )}
            </span>
          ))}

          <button
            type="button"
            onClick={openPicker}
            aria-haspopup="listbox"
            aria-expanded={open}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-amber-400/70 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors cursor-pointer"
          >
            <UserPlus className="h-2.5 w-2.5 shrink-0" aria-hidden />
            <span>{current.length > 0 ? 'Allocate another' : 'Allocate estimator'}</span>
            <ChevronDown className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden />
          </button>
        </>
      )}

      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 z-50 mt-1 w-64 max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg py-1"
        >
          {membersLoading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-3 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              <span className="text-[11px]">Loading teammates…</span>
            </div>
          ) : pickable.length === 0 ? (
            <div className="px-3 py-3 text-[11px] text-muted-foreground">
              {(members ?? []).length === 0
                ? 'No teammates available to allocate.'
                : 'Everyone available is already allocated.'}
            </div>
          ) : (
            <>
              {/* Section label — only meaningful when an estimators-first
                  default is in play. */}
              {hasEstimators && (
                <div className="px-3 pt-1 pb-1.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {showingAll ? 'All teammates' : 'Estimators'}
                </div>
              )}
              {visibleMembers.map((m) => {
                const busy = busyUserId === m.user_id
                return (
                  <button
                    key={m.user_id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    disabled={busyUserId !== null}
                    onClick={() => allocate(m)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/60 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
                      {(m.display_name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] text-foreground">{m.display_name}</span>
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {m.position || m.email}
                      </span>
                    </span>
                    {busy && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden />}
                  </button>
                )
              })}
              {/* Toggle — only when estimators are the default AND there are
                  non-estimators to reveal. */}
              {hasEstimators && others.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="mt-1 w-full border-t border-border px-3 py-1.5 text-left text-[10px] font-medium text-amber-700 dark:text-amber-300 hover:bg-muted/50"
                >
                  {showingAll
                    ? 'Show estimators only'
                    : `Show all teammates (${pickable.length})`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default EstimatorAllocator
